// Rolling session activity tracker. Backs the "log in only if you haven't
// used the app for a day" UX.
//
// Why this is needed in addition to Cognito's token validity:
//   - Cognito's refresh-token flow does NOT rotate the refresh token. Each
//     refresh returns a new access/id token but the original refresh token
//     keeps its original "issued-at + validity" hard expiry.
//   - So we keep refresh at 7 days (see tfvars) and run our own inactivity
//     timer client-side at the SAME 7-day window. Every API call records
//     "last activity"; once the gap exceeds the threshold we treat the
//     session as expired and force re-login the next time the user touches
//     anything. Net effect: a 7-day session that the refresh token silently
//     renews while the user keeps using the app, and a forced re-login only
//     after 7 days idle (or 7 days from login, when the refresh token itself
//     expires — Cognito does not rotate it).
//
// localStorage is the right home for this: it survives reloads (the
// inactivity clock is supposed to roll across sessions on the same device)
// and the value is a single number so quota isn't a concern.

const KEY = 'qlearn:lastActivityAt'

/**
 * 7 days — matched to the Cognito refresh-token validity (see tfvars) so the
 * client gate and the token ceiling expire together. Drives the
 * "logged-out if idle for ≥ X" boundary.
 */
export const MAX_INACTIVE_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Mark the session as active right now. Called from `request()` after a
 * successful authenticated API response so passive page-loads of cached
 * data don't count — actual interaction with the backend does.
 */
export function recordActivity(): void {
  try {
    localStorage.setItem(KEY, String(Date.now()))
  } catch {
    /* private mode / quota — silently fall through */
  }
}

/** Returns the timestamp of the last recorded activity, or null when none. */
export function getLastActivityAt(): number | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

/**
 * True when we have a recorded activity AND the gap from now exceeds
 * MAX_INACTIVE_MS. Returns false when no activity is recorded — that's the
 * first-ever session, the user is in the middle of authenticating, and we
 * shouldn't force re-login because we never started the clock.
 */
export function isInactivityExpired(): boolean {
  const last = getLastActivityAt()
  if (last === null) return false
  return Date.now() - last > MAX_INACTIVE_MS
}

/** Drop the activity record. Called from logout so the next session starts a fresh clock. */
export function clearActivity(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* silent */
  }
}
